import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { Observable, catchError, mergeMap, of, switchMap } from 'rxjs';
import { IntersectionStatus } from '../../directives/intersection.directive';
import { IUiPage, IUiPageSection } from '../../models/ui.interface';
import { MeService } from '../../services/me.service';
import { NavService } from '../../services/nav.service';
import { UiBtnFabComponent } from '../ui-btn-fab/ui-btn-fab.component';
import { UiPageSectionComponent } from '../ui-page-section/ui-page-section.component';
import { UiScrollableComponent } from '../ui-scrollable/ui-scrollable.component';
import { UiToolbarTopComponent } from '../ui-toolbar-top/ui-toolbar-top.component';

@Component({
  selector: 'view-page',
  templateUrl: './view-page.component.html',
  styleUrls: ['./view-page.component.css'],
  imports: [UiToolbarTopComponent, CdkScrollable, UiPageSectionComponent, AsyncPipe, UiBtnFabComponent]
})
export class ViewPageComponent
  extends UiScrollableComponent
  implements OnInit, OnDestroy {

  @ViewChild('content', { static: false })
  set content(element: ElementRef<HTMLElement>) {
    this.scrollElement = element;
  }

  @ViewChildren('sections')
  sections!: QueryList<UiPageSectionComponent>;

  // Page
  public page$!: Observable<IUiPage | null>;
  public page!: IUiPage;

  // Header
  public _headerTitle: string | undefined = undefined;
  public get headerTitle(): string | undefined {

    if (this.headerTextOnScroll && !this.scrolledMin())
      return undefined;

    return this._headerTitle;

  }

  public get headerTitleClass(): string {

    return 'title-large';

  }

  public get headerState(): string | undefined {

    if (this.scrolledMin() && !this.headerInline)
      return 'scrolled';

    return this.headerTheme;
  }

  public get headerInline(): boolean {
    return this.headerTheme?.includes('inline') || false;
  }

  public get headerFixed(): boolean {
    return (this.headerTheme && !this.headerInline) || false;
  }

  public get headerTextOnScroll(): boolean {
    return this.headerTheme?.includes('text-on-scroll') || false;
  }

  public get headerTheme(): string | undefined {

    if (this.page) {

      // first section
      if (this.page.sections?.length &&
        this.page.sections[0].headerTheme)
        return this.page.sections[0].headerTheme;

      // second page
      if (this.page.headerTheme)
        return this.page.headerTheme;

    }

    return undefined;

  }

  public get headerBackVisible(): boolean {

    if (this.page) {
      const segments = this.page.pageId.split('/');
      return _.last(segments) != 'home';
    }

    return false;
  }

  private _sanitizedHTML: any;
  public get contentHTML(): any {
    if (this.page.html) {
      if (!this._sanitizedHTML)
        this._sanitizedHTML = this.sanitizer.bypassSecurityTrustHtml(this.page.html);
      return this._sanitizedHTML;
    }
    else {
      this._sanitizedHTML = null;
      return null;
    }
  }


  constructor(public override me: MeService, private nav: NavService,
    renderer: Renderer2,
    host: ElementRef,
    protected route: ActivatedRoute,
    private title: Title,
    scrollDispatcher: ScrollDispatcher,
    ngZone: NgZone,
    private sanitizer: DomSanitizer) {
    super(me, renderer, host, scrollDispatcher, ngZone)
  }

  override ngOnInit(): void {

    // call base implementation
    super.ngOnInit();

    // Multiple http requests
    // https://stackoverflow.com/questions/56278367/multiple-identical-async-pipe-in-angular-causing-multiple-http-requests

    this.page$ = this.route.url.pipe(
      switchMap(url => {

        // defaults to home
        var nav = 'home';

        // concatenate url segments, e.g. 'about/gdpr'
        if (url.length)
          nav = _.map(url, urlSeg => urlSeg.path).join('/');

        // load page, error MUST be handled here
        return this.loadPage(nav).pipe(

          catchError(error => {

            // Handle or log the error
            console.error(error);

            // Return a fallback value or a new Observable
            return of(this.page); // Ensure this.page is a sensible fallback

          })
        );

      }),
      mergeMap(page => this.updatePage(page))
    );

    // set this component as current page
    this.me.currentPage = this;
  }

  ngAfterViewChecked() {

    // set image after tile is stabilized - problems with virtual scroll
    // measuring offsetWidth and offsetHeight breaks scrolling to top
    super.afterViewChecked(this.page);

  }

  ngOnDestroy(): void {

    // reset this component as current page
    this.me.currentPage = null;

  }


  private loadPage(pageId: string): Observable<IUiPage> {

    return this.me.call<IUiPage>('browse.page', {

      areaId: "public",
      pageId: pageId

    });

  }

  public reloadPage() {

    // defaults to home
    var nav = 'home';

    // concatenate url segments, e.g. 'about/gdpr'
    if (this.route.snapshot.url.length)
      nav = _.map(this.route.snapshot.url, urlSeg => urlSeg.path).join('/');

    this.loadPage(nav)
      .subscribe(page => this.updatePage(page));
  }

  private updatePage(page: IUiPage): Observable<IUiPage | null> {

    // nothing to do
    if (!page)
      return of(null);

    // update CSS and style
    super.update(page);

    // set page
    this.page = page;

    // set sanitized HTML
    this._sanitizedHTML = null;

    // set page title
    if (this.page.title) {
      this._headerTitle = this.page.title;
      this.title.setTitle(`${this.me.domainTitle} - ${this.page.title}`);
    }
    else
      if (this.me.domainTitle) {
        this._headerTitle = this.me.domainTitle;
        this.title.setTitle(this.me.domainTitle);
      }
      else {
        this._headerTitle = this.me.session.domainId;
        this.title.setTitle(this.me.session.domainId);
      }

    // scroll to top
    this.scrollToTop();

    return of(page);
  }

  public onVisibilityChanged(section: IUiPageSection, status: IntersectionStatus) {
    console.log(`section ${section} ${status}`);
  }

  public onSectionClick(section: IUiPageSection) {

    const navigate = section.navigate ?? section.data?.navigate;
    if (navigate) {

      // scroll or navigate
      if (navigate.startsWith('#'))
        this.scrollToSection(navigate.substring(1));
      else
        this.me.navigate(navigate);

    }
  }

  public scrollToSection(sectionId: string) {

    if (!this.page?.sections?.length)
      return;

    var idx = _.findIndex(this.page.sections, (section) => section.sectionId == sectionId);
    if (idx >= 0) {
      const element = this.sections.toArray()[idx].host.nativeElement;
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

  }

  // handle <a> element href clicks to router
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;

    // Check if the clicked element is an <a> tag or is inside one
    const anchorElement = targetElement.closest('a');

    if (anchorElement && anchorElement.href) {

      // handle situation, when href is SVGAnimatedString
      let href: string | null = null;

      // Check if anchorElement.href is an SVGAnimatedString
      if (anchorElement.href as any instanceof SVGAnimatedString) {
        // For SVGAnimatedString, use the baseVal
        href = (anchorElement.href as any).baseVal;
      } else if (typeof anchorElement.href === 'string') {
        // For standard string href
        href = anchorElement.getAttribute('href'); // Get the actual href value
      }

      // Determine if it's an internal Angular route
      // This logic might need to be adjusted based on your app's URL structure
      // and whether you use a base href.
      const currentOrigin = window.location.origin;

      if (href) {
        const isExternal = new URL(href, currentOrigin).origin !== currentOrigin;
        const isFragmentNavigation = href.startsWith('#');
        const isDownloadOrMailto = ['blob:', 'mailto:', 'tel:'].some(prefix => href.startsWith(prefix)) || anchorElement.hasAttribute('download');


        if (!isExternal && !isFragmentNavigation && !isDownloadOrMailto) {
          event.preventDefault(); // Prevent the default browser navigation (full page reload)

          // Use Angular Router to navigate.
          // router.navigateByUrl() is often good for href values.
          // It handles paths, query params, and fragments.
          this.nav.to(href);
        }
        // Else, allow default behavior for external links, fragment links, downloads, etc.
      }
    }
  }
}

